/**
 * Reads the org's extracted parties (the same grouped list Briefs builds) for linking to a
 * Client. Only parties carrying a real UID (id_type + id_number) can be linked, since that
 * pair is the identity/auto-link key. Richer fields (address, contacts) are surfaced when present
 * so this stays forward-compatible with the party-schema enrichment (see the EPIC doc §11).
 */
import { partyKey, inferPartyKind, type PartyKind } from '~~/shared/parties/partyKey'
import { formatIdentity } from '~~/shared/entities/vocabulary'
import type { PartyEntity } from '~~/shared/briefs/partyEntity'

export type { PartyKind }

export interface ExtractedParty {
  key: string
  name: string
  idType: string
  /** ISO 3166-1 alpha-2 issuer, or null when unknown (MAR-129). */
  jurisdiction: string | null
  idNumber: string
  /**
   * How the identity READS: the id type's label plus its country plus the number
   * ("Documento de identidad (SV): 01234567-8"). Composed once here so no surface renders the raw
   * enum token — after the migration `id_type` holds `national_id`, and every `{{ idType }}
   * {{ idNumber }}` in the UI was printing that at the user.
   */
  identityLabel: string
  kind: PartyKind
  roles: string[]
  documentCount: number
  /** The documents this party was extracted from. `GET /api/briefs/parties` already returns these
   *  (built from `entity_mentions`); they were previously reduced to a count and thrown away. */
  documents: Array<{ id: string; name: string }>
  importedAt: number
  /**
   * Forward-compatible enrichment, populated once the parser is extended. DECLARING THE RESPONSE
   * SHAPE SHOWED THESE HAVE NEVER BEEN POPULATED: the mapping below read `r.address` and
   * `r.contacts` off a grouped party that has no such fields, so the `?? null` / `?? []` fallback was
   * the only branch that ever ran. They are now written as the constants they always were, rather
   * than as a read that looks live.
   */
  address?: string | null
  contacts?: Array<{ name: string; role?: string | null }>
}

const NULLISH = new Set(['', 'null', 'undefined', 'n/a', '<unknown>'])
const isRealValue = (v: unknown): v is string => typeof v === 'string' && !NULLISH.has(v.trim().toLowerCase())

/**
 * When this party was first seen. Only the EXTRACTIONS carry a timestamp — a grouped party has no
 * `created_at` of its own, and `documents` is `{ id, name }`. Both of those were read here and both
 * were always `undefined`; the shape says so now.
 */
function importedAtOf(r: PartyEntity): number {
  const iso = r.extractions[0]?.created_at
  return iso ? new Date(iso).getTime() : 0
}

export function useParties() {
  const { apiFetch } = useApi()
  // useState-backed (not plain refs) so the two components that mount `useParties` together
  // (`clients/new.vue` + `PartyLinker.vue`) share ONE fetch of `/api/briefs/parties` instead of firing
  // it twice per render.
  const parties = useState<ExtractedParty[]>('parties-list', () => [])
  const allPartyRows = useState<PartyEntity[]>('parties-raw-rows', () => [])
  const loading = useState<boolean>('parties-loading', () => false)

  async function fetchParties(options: { force?: boolean } = {}) {
    // Share one fetch across co-mounted consumers: a concurrent call (loading) or an already-loaded
    // list short-circuits, unless the caller explicitly forces a refresh.
    if (loading.value) return
    if ((parties.value.length > 0 || allPartyRows.value.length > 0) && !options.force) return
    loading.value = true
    try {
      // The kind is known here, so the keyed map hands back `PartyEntity` — no narrowing, and no
      // second hand-written copy of the shape.
      const rows = await apiFetch<PartyEntity[]>('/api/briefs/parties')
      allPartyRows.value = rows || []
      parties.value = (rows || [])
        .flatMap<ExtractedParty>((r) => {
          // Only parties carrying a real UID can be linked; the pair is the identity key. Narrowed
          // here rather than in a `.filter`, which cannot tell TypeScript what it proved.
          const idType = r.id_type
          const idNumber = r.id_number
          if (!isRealValue(idType) || !isRealValue(idNumber)) return []
          return [{
            key: partyKey(idType, idNumber),
            name: r.name,
            idType,
            jurisdiction: r.jurisdiction ?? null,
            idNumber,
            identityLabel: formatIdentity(idType, r.jurisdiction, idNumber) ?? idNumber,
            kind: inferPartyKind(r.name, idType, r.party_kind),
            roles: r.roles,
            documentCount: r.documents.length,
            documents: r.documents.filter(d => d?.id).map(d => ({ id: String(d.id), name: String(d.name || d.id) })),
            importedAt: importedAtOf(r),
            address: null,
            contacts: [],
          }]
        })
        .sort((a, b) => b.importedAt - a.importedAt)
    } catch {
      allPartyRows.value = []
      parties.value = []
    } finally {
      loading.value = false
    }
  }

  return { parties, allPartyRows, loading, fetchParties }
}

